import { CNAPCGAAudit, Device } from '../../insights/types/InsightsTypes';

/**
 * Hierarchy level options for CGA hierarchy reporting
 */
export enum HierarchyLevel {
  DIRECT_REPORTS = 'DIRECT_REPORTS',
  FULL_REPORTING_HIERARCHY = 'FULL_REPORTING_HIERARCHY',
}

/**
 * Severity type for alert notifications
 */
export type Severity = 'success' | 'info' | 'warning' | 'error';

/**
 * Application interface for CGA hierarchy reporting
 */
export interface CGAAppItem {
  applicationId: number;
  name: string;
  description?: string;
  status: string;
  appGroup?: string;
  serviceLevelName?: string;
  serviceLevel?: number;
  serviceDomain?: string;
  owner: {
    login?: string;
    displayName: string;
    title?: string;
    department?: string;
  };
  createdAt?: string;
  updatedAt?: string;
  roles?: Array<{ role: { displayName: string } }>;
}

/**
 * Report metadata for audit evidence
 */
export interface ReportMetadata {
  generatedAt: string;
  generatedBy: string;
  hierarchyLevel: string;
  targetUser: string;
  totalApplications: number;
  fusionQuery: string;
  applicationIds: number[];
}

/**
 * Cloud account export type for formatting
 */
export interface CloudAccountExportType {
  'Application ID'?: number;
  'Application Name'?: string;
  'Account name': string;
  'Account ID': string;
  Platform: string;
  Environment: string;
  'Technical owner': string;
  'Associated applications': string;
}

/**
 * Resource data for an application (used for export)
 */
export interface AppResourceData {
  appId: number;
  appName: string;
  devices: Device[];
  cloudAccounts: CloudAccountExportType[];
  cnapData: CNAPCGAAudit[];
  owner: string;
  status: string;
  appGroup?: string;
  serviceDomain?: string;
}

/**
 * GraphQL response type for CGAHierarchyAppsQuery
 */
export type CGAHierarchyAppsQueryResponse = {
  identity?: {
    user?: {
      login?: string;
      displayName?: string;
      title?: string;
      department?: string;
      resources?: {
        applicationPortfolio?: {
          applications?: CGAAppItem[];
          totalCount?: number;
        };
      };
    };
  };
};

/**
 * Alert state interface
 */
export interface AlertState {
  open: boolean;
  message: string;
  severity: string;
}

/**
 * Target user display information
 */
export interface TargetUserDisplay {
  name: string;
  title: string;
  login: string;
}

/**
 * Export stage for progress tracking
 */
export type ExportStage =
  | 'idle'
  | 'graphql'
  | 'cnap'
  | 'retrying'
  | 'finalizing';

/**
 * Failed fetch entry for tracking retry attempts
 */
export interface FailedFetchEntry {
  appId: number;
  appName: string;
  errorMessage: string;
  attemptCount: number;
}

/**
 * Batch fetch result with success and failure tracking
 */
export interface BatchFetchResult {
  successful: AppResourceData[];
  failedEntries: FailedFetchEntry[];
}

/**
 * CNAP batch fetch result
 */
export interface CnapBatchFetchResult {
  data: Map<number, CNAPCGAAudit[]>;
  failedAppIds: number[];
}

/**
 * Device data from applicationsPaged query
 */
export interface ApplicationDevice {
  tier?: string;
  ipAddress?: string;
  hostname?: string;
  environment?: string;
  type?: string;
  fqdn?: string;
  href?: string;
  operationalStatus?: string;
  serialNumber?: string;
  make?: string;
  model?: string;
  osInfo?: {
    make?: string;
    name?: string;
    edition?: string;
    release?: string;
    version?: string;
  };
}

/**
 * Cloud account data from applicationsPaged query
 */
export interface ApplicationCloudAccount {
  id: string;
  name?: string;
  platform?: string;
  status?: string;
  environment?: string;
}

/**
 * Application entry from applicationsPaged query
 */
export interface ApplicationResourceEntry {
  applicationId: number;
  name: string;
  status: string;
  owner?: {
    displayName?: string;
  };
  cloudAccounts?: ApplicationCloudAccount[];
  devicesPaged?: {
    devices?: ApplicationDevice[];
    totalCount?: number;
  };
}

/**
 * Response type for the Application Resources query
 */
export interface ApplicationResourcesQueryResponse {
  resources?: {
    applicationsPaged?: {
      totalCount?: number;
      applications?: ApplicationResourceEntry[];
    };
  };
}
